import { Screen, ScreenConfig } from '../core/Screen';
import { EventBus, Events, APIManager, StateManager, EventPlaybackManager, ScreenRouter, CombatSummaryTracker } from '../../managers';
import { BACKGROUNDS, CB, getFullSpriteUrl, getPortraitUrl } from '../../combat/spriteConfig';
import { formatGold } from '../../utils/format';
import { elementColor } from '../../utils/elementColors';
import { Tooltip } from '../components/Tooltip';
import { buildAbilityTooltip, rarityColor as getRarityColor, formatStatNameFromDef } from '../components/TooltipContent';
import { formatAffixValue } from '../components/AffixDisplay';
import { shareButtonHTML, bindShareButtons } from '../components/ShareButton';
import { renderItemIcon, renderElementIcon, getAbilityIconUrl, renderCurrencyIcon, getElementIconUrl } from '../components/ItemIcon';
import { buildElementTooltip } from '../components/ElementTooltip';
import { renderUnitPortrait } from '../components/UnitPortrait';
import { CombatHUD } from '../components/CombatHUD';
import { formatUnitClass, formatRace, formatElement, formatCategory, formatDefensiveTypes } from '../components/GameTerms';
import { parseDiscoveredStrands } from '../components/StrandDiscoveryModal';
import { showDebriefDialogue, showDecisionScene } from '../components/NpcBriefingModal';
import { CHAPTER_BRIEFINGS } from '../../config/briefings';
import { ToastManager } from '../components/Toast';
import { reportFightSentiment } from '../../managers/Telemetry';

/** Sum total barrier amount across all instances on a combatant. */
function sumCombatantBarrier(c: { barriers?: ServerBarrierInstance[] } | undefined): number {
  if (!c?.barriers) return 0;
  let total = 0;
  for (const b of c.barriers) total += b.amount;
  return total;
}
import type {
  AbilityDef,
  AffixInstance,
  AffixDef,
  CombatSceneData,
  CombatantDef,
  ServerBarrierInstance,
  ServerCombatState,
  ServerCombatantState,
  CombatAction,
  CombatActionResult,
  CrawlData,
  ConfigData,
  CurrencyDef,
  CurrencyDrop,
  TimelineEntry,
  UnitDropData,
  ItemData,
} from '../../types';
import { SKILL_SLOT_COUNT } from '../../types';

type CombatPhase = 'combat' | 'victory' | 'defeat' | 'crawl_complete' | 'fork_choice';

interface CombatScreenState {
  phase: CombatPhase;
  isPlayingEvents: boolean;
}

export class CombatScreen extends Screen<CombatScreenState> {
  readonly screenConfig: ScreenConfig = {
    id: 'combat',
    name: 'Combat',
    persistent: true,
    needsPhaser: true,
  };

  // Override hide/show to NO-OP — battle-container visibility is managed
  // by ScreenRouter.showContainer() using visibility:hidden instead of
  // display:none. The base UIComponent.hide() adds .hidden (display:none
  // !important) which causes Phaser's ScaleManager to resize the WebGL
  // canvas to 0x0, corrupting GPU texture state.
  hide(): void { /* managed by showContainer */ }
  show(): void { /* managed by showContainer */ }

  private bindDebugBtn(id: string, handler: () => void): void {
    const btn = document.getElementById(id);
    if (btn && !btn.dataset.bound) {
      btn.dataset.bound = '1';
      btn.addEventListener('click', handler);
    }
  }

  private elements = {
    abilityButtons: null as HTMLElement | null,
    activeCharName: null as HTMLElement | null,
    activeCharClass: null as HTMLElement | null,
    activeCharPortrait: null as HTMLElement | null,
    zoneName: null as HTMLElement | null,
    encounterMap: null as HTMLElement | null,
    actionLog: null as HTMLElement | null,
    actionPanel: null as HTMLElement | null,
    combatHud: null as HTMLElement | null,
    phaserCanvas: null as HTMLElement | null,
  };

  private combatHUD: CombatHUD | null = null;
  private speedTimeline: import('../components/SpeedTimeline').SpeedTimeline | null = null;
  private floatingText: import('../components/FloatingCombatText').FloatingCombatText | null = null;
  private pendingAbility: AbilityDef | null = null;
  private currentAbilities: AbilityDef[] = [];
  private phaserInitialized = false;
  private hudInitialized = false;
  private activeCombatId: string | null = null;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private pendingUnitDrop: UnitDropData | null = null;
  private pendingItemDrop: ItemData | null = null;
  private pendingExtraItemDrops: ItemData[] = [];
  private pendingCurrencyDrops: CurrencyDrop[] = [];
  private pendingSkinDrop: { skinId: string; skinName: string; unitId: string; unitName: string } | null = null;
  private pendingForkPaths: Record<string, { label: string; description: string; hints: string[]; elements: string[] }> | null = null;
  private activeMaxFocus = 0;
  private celebrationStarted = false;
  private victoryBannerShown = false;
  private pendingRewards: { xp: number; gold: number; unitXp?: number } | null = null;
  private pendingUnitLevelUps: { unitId: string; unitName: string; oldLevel: number; newLevel: number; masteryPointsGained?: number }[] | null = null;
  /** Snapshot of player's discoveredStrands captured BEFORE a crawl advance, so
   *  the Strand Discovery reveal modal can detect first-time drops correctly. */
  private preAdvanceDiscoveredStrands: Set<string> = new Set();

  constructor() {
    super({ rootId: 'battle-container' });
    this.localState = {
      phase: 'combat',
      isPlayingEvents: false,
    };
  }

  onEnter(params?: Record<string, unknown>): void {
    this.showCombatUI(true);

    // Re-show debug bar (onLeave hides it)
    const debugBar = document.getElementById('combat-debug-bar');
    if (debugBar && window.location.hostname === 'localhost') {
      debugBar.style.display = '';
    }

    if (params?.combatResult) {
      // Fresh crawl start — reset summary tracker and set up the first encounter
      CombatSummaryTracker.reset();
      const combatResult = params.combatResult as CombatActionResult;

      const run = params.run as CrawlData['run'];
      const crawlData = StateManager.get('crawlData') as CrawlData | undefined;
      if (crawlData && run) {
        StateManager.set('crawlData', { ...crawlData, run });
      }

      this.restoreActionPanel();
      this.clearActionLog();
      this.localState!.phase = 'combat';

      const sceneData = this.buildSceneData(combatResult.combatState);
      // Only start/restart if this is a different combat than what's already loaded
      // (onCombatDataChange may have already initialized the scene during startup)
      if (combatResult.combatState.id !== this.activeCombatId) {
        this.activeCombatId = combatResult.combatState.id;
        this.startOrRestartScene(sceneData);
      }

      this.playInitialEvents(combatResult);
    }
  }

  onLeave(): void {
    this.hideCombatUI();
    document.getElementById('defeat-overlay')?.remove();
    const debugBar = document.getElementById('combat-debug-bar');
    if (debugBar) debugBar.style.display = 'none';
  }

  protected cacheElements(): void {
    this.elements = {
      abilityButtons: document.getElementById('ability-buttons'),
      activeCharName: document.getElementById('active-char-name'),
      activeCharClass: document.getElementById('active-char-class'),
      activeCharPortrait: document.getElementById('active-char-portrait') as HTMLImageElement | null,
      zoneName: document.getElementById('zone-name'),
      encounterMap: document.getElementById('encounter-map'),
      actionLog: document.getElementById('action-log'),
      actionPanel: document.getElementById('action-panel'),
      combatHud: document.getElementById('combat-hud'),
      phaserCanvas: document.getElementById('phaser-canvas'),
    };

    // Create CombatHUD eagerly so target picker is available before the scene finishes loading
    this.combatHUD = new CombatHUD();
    this.combatHUD.initialize();

  }

  protected bindEvents(): void {
    // Show debug bar on localhost
    const debugBar = document.getElementById('combat-debug-bar');
    if (debugBar && window.location.hostname === 'localhost') {
      debugBar.style.display = '';
      this.bindDebugBtn('cdb-kill', async () => {
        const result = await APIManager.debugKillAll();
        if (result.success && result.data) {
          const { combatState, events } = result.data;
          CombatSummaryTracker.ingestEvents(events, combatState);
          await EventPlaybackManager.playEvents(events, combatState);
          StateManager.set('combatData', combatState);
        }
      });
      this.bindDebugBtn('cdb-item', () => APIManager.adminGrantItem());
      this.bindDebugBtn('cdb-unit', () => APIManager.adminGrantUnit());
      this.bindDebugBtn('cdb-currency', () => APIManager.adminGrantCurrencies());
      this.bindDebugBtn('cdb-skip', () => APIManager.adminSkipCampaignStep());
      this.bindDebugBtn('cdb-reset-crawl', async () => {
        await APIManager.resetEventCrawl();
        const result = await APIManager.fetchUnifiedState();
        if (result.success && result.data) StateManager.batchUpdate(result.data);
      });
    }

    EventBus.on('COMBAT_SCENE_READY', () => {
      console.log('[CombatScreen] Combat scene ready');
      this.initHUDComponents();
      // Hide combat loading overlay — scene has rendered
      const loadingOverlay = document.getElementById('combat-loading');
      if (loadingOverlay) loadingOverlay.classList.add('hidden');
    });

    EventBus.on('COMBAT_TARGET_SELECTED', (targetId: unknown) => {
      this.onTargetSelected(targetId as string);
    });

    EventBus.on('COMBAT_TARGET_HOVERED', (targetId: unknown) => {
      this.onTargetHovered(targetId as string | null);
    });

    EventBus.on('COMBAT_CANCEL_TARGETING', () => {
      this.clearTargetingMode();
    });

    // Mid-playback timeline reshuffle (e.g. after a stagger that re-ordered turns).
    // Fires before each turn_start so the new order is visible before the actor moves.
    EventBus.on(Events.COMBAT_TIMELINE_RESORT, (payload: unknown) => {
      const { timeline, activeId } = payload as { timeline: string[]; activeId: string };
      this.updateTimelineFromSnapshot(timeline, activeId);
    });

    // Listen for combat log messages from EventPlaybackManager. Payload is
    // structured ({ message, type, isCrit, sourceSide }) so we can colorize the
    // row by category AND by who's attacking — older callers (internal
    // updateActionLog calls) still pass a plain string as a fallback.
    EventBus.on(Events.COMBAT_LOG, (payload: unknown) => {
      const data = typeof payload === 'string'
        ? { message: payload, type: undefined as string | undefined, isCrit: false, sourceSide: undefined as 'player' | 'enemy' | undefined }
        : (payload as { message: string; type?: string; isCrit?: boolean; sourceSide?: 'player' | 'enemy' });
      if (!data?.message) return;
      // Skip turn announcements from the compact action log
      if (data.message.endsWith("'s turn!")) return;
      this.updateActionLog(data);
    });

    // Victory/defeat from event playback
    EventBus.on(Events.COMBAT_VICTORY, (payload: unknown) => {
      this.clearTargetingMode();
      // Capture rewards from playback (StateManager may not be updated yet)
      if (payload && typeof payload === 'object') {
        this.pendingRewards = payload as { xp: number; gold: number; unitXp?: number };
      }
      if (this.localState) {
        this.localState.phase = 'victory';
        this.onRender();
      }
    });

    EventBus.on(Events.COMBAT_DEFEAT, () => {
      this.clearTargetingMode();
      if (this.localState) {
        this.localState.phase = 'defeat';
        this.onRender();
      }
      // Nudge the player to spend any unspent mastery before they retry —
      // new players often forget the system exists.
      const unitData = StateManager.get('unitData') as { party?: { unspentMasteryPoints?: number }[] } | undefined;
      const unspent = unitData?.party?.reduce((sum, u) => sum + (u.unspentMasteryPoints ?? 0), 0) ?? 0;
      if (unspent > 0) {
        ToastManager.show({
          type: 'info',
          message: `You have ${unspent} unspent Mastery ${unspent === 1 ? 'Point' : 'Points'} — invest them before your next run.`,
          duration: 6000,
        });
      }
    });

    EventBus.on(Events.COMBAT_FLEE_SUCCESS, () => {
      this.updateActionLog('You escaped!');
    });

    // Live Fervor pip updates during event playback (before StateManager full sync)
    EventBus.on(Events.FERVOR_UPDATE, (payload: unknown) => {
      const { fervor } = payload as { fervor: number };
      this.renderFervorPips(fervor);
    });

    // Update action panel focus bar when focus changes during playback
    EventBus.on(Events.HP_UPDATE, (payload: unknown) => {
      const { focus } = payload as { id: string; focus?: number };
      if (focus == null || !this.activeMaxFocus) return;
      const focusFill = document.getElementById('active-focus-fill');
      const focusText = document.getElementById('active-focus-text');
      if (!focusFill) return;
      const prevPct = parseFloat(focusFill.style.width) || 0;
      const pct = Math.max(0, Math.min(100, (focus / this.activeMaxFocus) * 100));
      // Only animate downward (spending). Regen (upward) sets instantly — no ping-pong.
      if (pct >= prevPct) {
        focusFill.style.transition = 'none';
        focusFill.style.width = `${pct}%`;
        void focusFill.offsetWidth;
        focusFill.style.transition = '';
      } else {
        focusFill.style.width = `${pct}%`;
      }
      if (focusText) focusText.textContent = `${Math.floor(focus)}/${this.activeMaxFocus}`;
    });

    // Dev debug menu — disabled for now
    // To re-enable: uncomment and set a condition (e.g. hostname === 'localhost')

    // Keyboard hotkeys
    this.keydownHandler = (e: KeyboardEvent) => this.handleKeydown(e);
    document.addEventListener('keydown', this.keydownHandler);
  }

  protected setupSubscriptions(): void {
    this.subscribeState('combatData', this.onCombatDataChange.bind(this));
    this.subscribeState('crawlData', this.onCrawlDataChange.bind(this));
  }

  protected onRender(): void {
    if (!this.localState) return;

    const { phase, isPlayingEvents } = this.localState;

    if (phase === 'victory') {
      this.renderVictory();
      return;
    }

    if (phase === 'defeat') {
      this.renderDefeat();
      return;
    }

    if (phase === 'crawl_complete') {
      this.startCrawlCelebration();
      return;
    }

    if (phase === 'fork_choice') {
      this.renderForkChoice();
      return;
    }

    // Active combat
    const combatData = StateManager.get('combatData');
    if (!combatData) return;

    const active = combatData.combatants.find(c => c.id === combatData.activeCombatantId);

    // Update active character info + portrait
    if (this.elements.activeCharName && active) {
      this.elements.activeCharName.textContent = active.name;
    }
    if (this.elements.activeCharClass) {
      this.elements.activeCharClass.textContent = active?.unitClass
        ? formatUnitClass(active.unitClass)
        : '';
    }
    if (this.elements.activeCharPortrait && active?.spriteKey) {
      this.elements.activeCharPortrait.innerHTML = renderUnitPortrait({
        spriteKey: active.spriteKey,
        name: active.name,
        size: 'sm',
      });
      this.elements.activeCharPortrait.style.display = '';
    } else if (this.elements.activeCharPortrait) {
      this.elements.activeCharPortrait.style.display = 'none';
    }

    // Render ability buttons for active player character
    this.renderAbilityButtons(combatData, active ?? null);

    // Update Fervor display
    this.updateFervorDisplay(combatData);

    // Enable/disable buttons based on turn + playback state
    const isPlayerTurn = active?.side === 'player';
    this.setButtonsEnabled(isPlayerTurn && !isPlayingEvents);

    // Update timeline
    this.updateTimeline(combatData);
  }

  // ---------------------------------------------------------------------------
  // State change handlers
  // ---------------------------------------------------------------------------

  private onCombatDataChange(combatData: ServerCombatState | null | undefined): void {
    if (!this.localState) return;

    if (!combatData) {
      this.activeCombatId = null;
      // Only navigate away if we're the active screen and in combat phase
      if (ScreenRouter.getActiveScreenId() === 'combat' && this.localState.phase === 'combat') {
        ScreenRouter.replace('main-menu');
      }
      return;
    }

    // We have combat data
    if (combatData.status === 'active') {
      if (this.localState.phase !== 'combat') {
        this.localState.phase = 'combat';
      }
      // Only start/restart scene for a NEW combat encounter, not after every action
      if (combatData.id !== this.activeCombatId) {
        this.activeCombatId = combatData.id;
        this.restoreActionPanel();
        const sceneData = this.buildSceneData(combatData);
        this.startOrRestartScene(sceneData);
      }
    }

    this.onRender();
  }

  private onCrawlDataChange(crawlData: CrawlData | null | undefined): void {
    if (!crawlData) return;

    // Update top bar
    if (this.elements.zoneName) {
      this.elements.zoneName.textContent = crawlData.config.name;
    }

    // Zone modifiers indicator
    const zoneModsEl = document.getElementById('zone-modifiers');
    if (zoneModsEl) {
      const mods = crawlData.config.zoneModifiers;
      if (mods && mods.length > 0) {
        const MOD_LABELS: Record<string, string> = {
          smothered: 'Smothered: -50% Healing',
          draining: 'Draining: +15% Focus Costs',
          fortified: 'Fortified: +50% Monster Life',
        };
        zoneModsEl.innerHTML = mods.map(m =>
          `<span class="zone-mod-tag">${MOD_LABELS[m] ?? m}</span>`
        ).join('');
        zoneModsEl.classList.remove('hidden');
      } else {
        zoneModsEl.classList.add('hidden');
      }
    }

    // Show/hide combat menu button
    const menuBtn = document.getElementById('combat-menu-btn');
    if (menuBtn) {
      menuBtn.classList.toggle('hidden', !crawlData.run);
      if (!menuBtn.dataset.bound) {
        menuBtn.dataset.bound = '1';
        menuBtn.addEventListener('click', () => this.openCombatMenu());
      }
    }
    if (crawlData.run) {
      const total = crawlData.config.encounters.length;
      const current = crawlData.run.encounterIndex;
      this.updateEncounterMap(current, total);
    }
  }

  private updateEncounterMap(current: number, total: number): void {
    if (!this.elements.encounterMap) return;
    this.elements.encounterMap.innerHTML = '';

    for (let i = 0; i < total; i++) {
      if (i > 0) {
        const line = document.createElement('span');
        line.className = 'enc-line';
        if (i <= current) line.classList.add('completed');
        this.elements.encounterMap.appendChild(line);
      }

      const node = document.createElement('span');
      node.className = 'enc-node';
      if (i < current) {
        node.classList.add('completed');
      } else if (i === current) {
        node.classList.add('current');
      }
      this.elements.encounterMap.appendChild(node);
    }
  }

  // ---------------------------------------------------------------------------
  // Scene data building
  // ---------------------------------------------------------------------------

  private buildSceneData(combatState: ServerCombatState): CombatSceneData {
    const players: CombatantDef[] = combatState.combatants
      .filter(c => c.side === 'player' && c.isAlive)
      .map(c => ({
        id: c.id,
        spriteKey: c.spriteKey,
        name: c.name,
        hp: c.hp,
        maxHp: c.maxHp,
      }));

    const enemies: CombatantDef[] = combatState.combatants
      .filter(c => c.side === 'enemy' && c.isAlive)
      .map(c => ({
        id: c.id,
        spriteKey: c.spriteKey,
        name: c.name,
        hp: c.hp,
        maxHp: c.maxHp,
      }));

    const crawlData = StateManager.get('crawlData') as CrawlData | undefined;
    const encounterIdx = crawlData?.run?.encounterIndex ?? 0;
    const encounterBgKey = crawlData?.config?.encounters?.[encounterIdx]?.bgKey;
    const bgKey = encounterBgKey ?? crawlData?.config?.bgKey;

    const lightingType = crawlData?.config?.lightingType;

    const mordantColor = combatState.mordantColor as CombatSceneData['mordantColor'] | undefined;

    return { players, enemies, activeCombatantId: combatState.activeCombatantId, bgKey, lightingType, mordantColor };
  }

  private startOrRestartScene(sceneData: CombatSceneData): void {
    if (!this.phaserInitialized) {
      this.phaserInitialized = true;
    }
    this.hudInitialized = false;
    // Always go through INIT_PHASER — it handles both first init and restart,
    // and reliably passes scene data via pendingSceneData (Phaser's
    // scene.settings.data from scene.restart() is unreliable).
    EventBus.emit('INIT_PHASER', sceneData);

    this.showCombatUI();
  }

  private showCombatUI(showLoading = false): void {
    if (this.elements.phaserCanvas) {
      this.elements.phaserCanvas.style.visibility = '';
      this.elements.phaserCanvas.style.pointerEvents = '';
    }
    const topBar = document.getElementById('combat-top-bar');
    const floatingHud = document.getElementById('floating-hud');
    if (topBar) topBar.style.display = '';
    if (floatingHud) floatingHud.style.display = '';
    // Only show loading overlay on initial scene start, not room transitions
    if (showLoading) {
      const loadingOverlay = document.getElementById('combat-loading');
      if (loadingOverlay) loadingOverlay.classList.remove('hidden');
    }
  }

  private hideCombatUI(): void {
    if (this.elements.phaserCanvas) {
      this.elements.phaserCanvas.style.visibility = 'hidden';
      this.elements.phaserCanvas.style.pointerEvents = 'none';
    }
    const topBar = document.getElementById('combat-top-bar');
    const floatingHud = document.getElementById('floating-hud');
    if (topBar) topBar.style.display = 'none';
    if (floatingHud) floatingHud.style.display = 'none';
  }

  // ---------------------------------------------------------------------------
  // Event playback helper
  // ---------------------------------------------------------------------------

  /** Returns immediately if the HUD is already initialized, otherwise waits for it. */
  private waitForSceneReady(): Promise<void> {
    if (this.hudInitialized) return Promise.resolve();
    return new Promise(resolve => {
      // Listen for both events: COMBAT_SCENE_READY (first init) and
      // HUD_INITIALIZED (room transitions where COMBAT_SCENE_READY already fired).
      const done = () => {
        offScene();
        offHud();
        resolve();
      };
      const offScene = EventBus.on('COMBAT_SCENE_READY', done);
      const offHud = EventBus.on('HUD_INITIALIZED', done);
    });
  }

  private async playInitialEvents(combatResult: CombatActionResult): Promise<void> {
    this.localState!.isPlayingEvents = true;
    // Set combat data BEFORE playback so HUD components can read it during init
    StateManager.set('combatData', combatResult.combatState);
    // Feed events into summary tracker
    CombatSummaryTracker.ingestEvents(combatResult.events, combatResult.combatState);
    // Wait for Phaser scene + HUD to be ready before animating
    await this.waitForSceneReady();
    await EventPlaybackManager.playEvents(combatResult.events, combatResult.combatState);
    this.localState!.isPlayingEvents = false;

    // Update state (won't trigger another restart since activeCombatId matches)
    StateManager.set('combatData', combatResult.combatState);
    this.onRender();
  }

  // ---------------------------------------------------------------------------
  // HUD initialization
  // ---------------------------------------------------------------------------

  private async initHUDComponents(): Promise<void> {
    const { SpeedTimeline } = await import('../components/SpeedTimeline');
    const { FloatingCombatText } = await import('../components/FloatingCombatText');

    // Destroy/recreate scene-dependent HUD components when restarting
    this.combatHUD?.destroy();
    this.combatHUD = new CombatHUD();
    this.combatHUD.initialize();

    this.speedTimeline?.destroy();
    this.speedTimeline = new SpeedTimeline();
    this.speedTimeline.initialize();

    this.floatingText?.destroy();
    this.floatingText = new FloatingCombatText();
    this.floatingText.initialize();

    // Build combatants from server state
    const combatData = StateManager.get('combatData');
    if (combatData) {
      const combatants = combatData.combatants
        .filter(c => c.isAlive)
        .map(c => ({
          id: c.id,
          name: c.name,
          current: c.hp,
          max: c.maxHp,
          shield: sumCombatantBarrier(c), barriers: c.barriers ?? [],
          isEnemy: c.side === 'enemy',
          elements: (c.defensiveTypes && c.defensiveTypes.length > 0) ? c.defensiveTypes : (c.element ? [c.element] : undefined),
          statusEffects: c.statusEffects,
          tempo: c.tempo,
          focus: c.focus,
          maxFocus: c.maxFocus,
          breakFill: c.breakFill,
          breakThreshold: c.breakThreshold,
          breakBarRevealed: c.breakBarRevealed,
        }));
      this.combatHUD.setCombatants(combatants);
      this.updateTimeline(combatData);

      // Sync persistent status effect visuals (bleed/burn/poison/etc. glows) to
      // the Phaser scene. STATUS_EFFECTS_UPDATE is normally only emitted during
      // animation playback (apply/expire), so on a cold load (hard refresh mid-
      // combat, room transitions) existing effects would otherwise stay invisible
      // until the next tick event.
      for (const c of combatData.combatants) {
        if (!c.isAlive) continue;
        if (c.statusEffects && c.statusEffects.length > 0) {
          EventBus.emit(Events.STATUS_EFFECTS_UPDATE, {
            id: c.id,
            statusEffects: c.statusEffects,
          });
        }
      }
    }

    this.hudInitialized = true;
    this.onRender();

    // Signal CombatScene to re-emit positions now that HUD is subscribed
    EventBus.emit('HUD_INITIALIZED');
  }

  private updateTimeline(combatData: ServerCombatState): void {
    if (!this.speedTimeline) return;

    const entries = combatData.timeline
      .map(id => {
        const c = combatData.combatants.find(x => x.id === id);
        if (!c || !c.isAlive) return null;
        return {
          id: c.id,
          label: c.name[0],
          isEnemy: c.side === 'enemy',
          isActive: c.id === combatData.activeCombatantId,
          spriteKey: c.spriteKey,
          momentum: c.momentum,
          spd: c.spd,
          side: c.side,
          slotIndex: c.slotIndex,
        };
      })
      .filter(Boolean) as TimelineEntry[];

    this.speedTimeline.setTurnOrder(entries);
  }

  /**
   * Reorder the timeline strip from a backend snapshot during event playback.
   * StateManager.combatData still holds the pre-action state, but the static
   * lookup fields (sprite/name/side/slot) we need are unchanged — only the
   * order and active flag come from the snapshot.
   */
  private updateTimelineFromSnapshot(timeline: string[], activeId: string): void {
    if (!this.speedTimeline) return;
    const combatData = StateManager.get('combatData') as ServerCombatState | undefined;
    if (!combatData) return;

    const entries = timeline
      .map(id => {
        const c = combatData.combatants.find(x => x.id === id);
        if (!c) return null;
        return {
          id: c.id,
          label: c.name[0],
          isEnemy: c.side === 'enemy',
          isActive: c.id === activeId,
          spriteKey: c.spriteKey,
          momentum: c.momentum,
          spd: c.spd,
          side: c.side,
          slotIndex: c.slotIndex,
        };
      })
      .filter(Boolean) as TimelineEntry[];

    this.speedTimeline.setTurnOrder(entries);
  }

  // ---------------------------------------------------------------------------
  // Ability buttons
  // ---------------------------------------------------------------------------

  private renderAbilityButtons(
    _combatData: ServerCombatState,
    active: ServerCombatantState | null,
  ): void {
    if (!this.elements.abilityButtons) return;

    // Old triggers are being destroyed — dismiss any open tooltip
    Tooltip.hide();
    this.elements.abilityButtons.innerHTML = '';

    // Update focus bar in action panel
    this.updateActionPanelFocus(active);

    if (!active || active.side !== 'player') {
      this.currentAbilities = [];
      return;
    }

    const configData = StateManager.get('configData') as ConfigData | undefined;
    if (!configData) return;

    const abilities = this.getAbilities(active);
    this.currentAbilities = abilities;

    for (let i = 0; i < abilities.length; i++) {
      const ability = abilities[i];
      const abilityConfig = configData.abilities[ability.id];
      const color = abilityConfig ? elementColor(abilityConfig.element) : '#cccccc';

      // Look up focus cost from the combatant's skills
      const skill = active.skills?.find(g => g.abilityId === ability.id);
      const focusCost = skill?.focusCost ?? 0;
      const canAfford = active.focus >= focusCost;

      // Hard cooldown remaining (turns until usable). Takes visual precedence over
      // insufficient-focus — the CD readout is more actionable than the focus cost.
      const cooldownRemaining = active.abilityCooldowns?.[ability.id] ?? 0;
      const onCooldown = cooldownRemaining > 0;

      const btn = document.createElement('button');
      btn.className = 'combat-btn ability-btn';
      if (this.pendingAbility?.id === ability.id) btn.classList.add('selected');
      if (onCooldown) {
        btn.classList.add('on-cooldown');
        btn.disabled = true;
      } else if (!canAfford) {
        btn.classList.add('insufficient-focus');
        btn.disabled = true;
      }
      btn.type = 'button';
      btn.dataset.abilityId = ability.id;
      btn.style.setProperty('--btn-accent', color);

      const iconSrc = getAbilityIconUrl(ability.id);
      const costHtml = focusCost > 0
        ? `<span class="ability-btn-cost">${focusCost}</span>`
        : '';
      const cooldownHtml = onCooldown
        ? `<div class="ability-btn-cooldown">${cooldownRemaining}</div>`
        : '';
      btn.innerHTML = `
        <span class="ability-btn-hotkey">${i + 1}</span>
        <div class="ability-btn-content">
          <div class="ability-btn-icon-wrap">
            <img class="ability-btn-icon" src="${iconSrc}" alt="" width="36" height="36" draggable="false" onerror="this.style.display='none'">
            ${cooldownHtml}
          </div>
          <div class="ability-btn-label">
            <span class="ability-btn-name">${ability.name}</span>
            ${costHtml}
          </div>
        </div>
      `;
      btn.addEventListener('click', () => this.onAbility(ability));
      btn.addEventListener('mouseenter', () => this.showTimelinePreview(ability));
      btn.addEventListener('mouseleave', () => this.clearTimelinePreview());

      // Attach tooltip
      if (abilityConfig) {
        Tooltip.attach(btn, () =>
          buildAbilityTooltip(abilityConfig, active, configData.statusEffects),
        );
      }

      this.elements.abilityButtons.appendChild(btn);
    }

    // If no abilities are usable (focus-short OR on cooldown), show Skip Turn button.
    // Cooldowns can lock every ability even when the player has full focus, so the check
    // has to consider both gates — otherwise the player has no legal action.
    if (active.maxFocus > 0) {
      const anyUsable = abilities.some((ability) => {
        const skill = active.skills?.find(g => g.abilityId === ability.id);
        const focusCost = skill?.focusCost ?? 0;
        const cdRemaining = active.abilityCooldowns?.[ability.id] ?? 0;
        return active.focus >= focusCost && cdRemaining === 0;
      });
      if (!anyUsable) {
        const skipBtn = document.createElement('button');
        skipBtn.className = 'combat-btn ability-btn skip-turn-btn';
        skipBtn.type = 'button';
        skipBtn.innerHTML = `<div class="ability-btn-content"><span class="ability-btn-name">Skip Turn</span></div>`;
        skipBtn.addEventListener('click', () => this.onSkipTurn());
        this.elements.abilityButtons.appendChild(skipBtn);
      }
    }

    // Pad to SKILL_SLOT_COUNT — show empty placeholders for unequipped skill slots
    const totalSlots = SKILL_SLOT_COUNT;
    const filledSlots = this.elements.abilityButtons.children.length;
    for (let s = filledSlots; s < totalSlots; s++) {
      const empty = document.createElement('div');
      empty.className = 'ability-slot-empty';
      this.elements.abilityButtons.appendChild(empty);
    }
  }

  private updateActionPanelFocus(active: ServerCombatantState | null): void {
    const focusRow = document.getElementById('active-focus-row');
    const focusFill = document.getElementById('active-focus-fill');
    const focusText = document.getElementById('active-focus-text');
    if (!focusRow) return;

    if (!active || active.side !== 'player' || !active.maxFocus || active.maxFocus <= 0) {
      this.activeMaxFocus = 0;
      focusRow.style.display = 'none';
      return;
    }

    this.activeMaxFocus = active.maxFocus;
    focusRow.style.display = '';
    const pct = Math.max(0, Math.min(100, (active.focus / active.maxFocus) * 100));
    if (focusFill) {
      // Skip transition on unit swap — set instantly, then re-enable
      focusFill.style.transition = 'none';
      focusFill.style.width = `${pct}%`;
      void focusFill.offsetWidth; // force reflow
      focusFill.style.transition = '';
    }
    if (focusText) focusText.textContent = `${active.focus}/${active.maxFocus}`;
  }

  /** Render the Fervor pip track (inline in the focus row). Hidden when empty.
   *  Pass the current value directly when playing live events so pips update
   *  mid-playback — only newly-activated pips get the pop-in animation.
   *  decayTimer (0-2) is the count of player turns since last gain — at 3 the
   *  pool loses a stack and the timer resets. Shown in the tooltip so players
   *  can plan when to bank a spender before stacks vanish. */
  private renderFervorPips(fervor: number, decayTimer: number = 0): void {
    const pips = document.getElementById('fervor-pips');
    if (!pips) return;

    if (fervor <= 0) {
      pips.style.display = 'none';
      pips.innerHTML = '';
      return;
    }

    const FERVOR_CAP = 5;
    const prevActive = pips.querySelectorAll('.fervor-pip.active').length;
    const prevTotal = pips.childElementCount;
    // Skip pip rebuild if nothing changed, but still refresh the tooltip in case
    // decayTimer changed (it often does across turns even when fervor doesn't).
    const pipsUnchanged = prevTotal === FERVOR_CAP && prevActive === fervor && pips.style.display !== 'none';

    if (!pipsUnchanged) {
      let html = '';
      for (let i = 0; i < FERVOR_CAP; i++) {
        const active = i < fervor;
        const isNew = active && i >= prevActive;
        html += `<span class="fervor-pip${active ? ' active' : ''}${isNew ? ' fervor-pip--new' : ''}"></span>`;
      }
      pips.innerHTML = html;
      pips.style.display = '';
    }

    // Decay countdown: turns remaining before next stack loss (3 - current timer).
    // Guard against cap drift by clamping to 1-3.
    const turnsUntilDecay = Math.max(1, Math.min(3, 3 - decayTimer));
    Tooltip.attach(pips, () =>
      `<div class="tooltip-inner">` +
        `<div class="tooltip-header">` +
          `<span class="tooltip-name">Fervor</span>` +
          `<span class="tooltip-element">${fervor}/${FERVOR_CAP}</span>` +
        `</div>` +
        `<div class="tooltip-line"><span class="label">Party-wide resource. Built by outcome triggers (kills, stealth strikes, Roar, weakness hits). Consumed by spender abilities for massive bonus damage per stack.</span></div>` +
        `<div class="tooltip-separator"></div>` +
        `<div class="tooltip-line"><span class="label">Loses 1 stack in <strong class="damage">${turnsUntilDecay}</strong> player turn${turnsUntilDecay === 1 ? '' : 's'} without a gain.</span></div>` +
      `</div>`
    );
  }

  private updateFervorDisplay(combatData: ServerCombatState): void {
    this.renderFervorPips(combatData.fervor ?? 0, combatData.fervorDecayTimer ?? 0);
  }

  private getAbilities(
    active: ServerCombatantState,
  ): AbilityDef[] {
    const abilities: AbilityDef[] = [];

    // Use skills if available (player units with equipped skills)
    if (active.skills && active.skills.length > 0) {
      for (const s of active.skills) {
        abilities.push({
          id: s.abilityId,
          name: s.name,
          action: s.type === 'damage' ? 'COMBAT_ATTACK' : 'COMBAT_SKILL',
          targeting: this.mapTargeting(s.target),
        });
      }
      return abilities;
    }

    return abilities;
  }

  private mapTargeting(target: string): 'single' | 'all' | 'self' | 'ally_single' | 'ally_all' {
    switch (target) {
      case 'single_enemy': return 'single';
      case 'chain_enemy': return 'single';
      case 'adjacent_enemies': return 'single'; // player picks target, engine hits adjacent
      case 'all_enemies': return 'all';
      case 'single_ally': return 'ally_single';
      case 'all_allies': return 'ally_all';
      case 'self': return 'self';
      default: return 'single';
    }
  }

  // ---------------------------------------------------------------------------
  // Action handling
  // ---------------------------------------------------------------------------

  private onAbility(ability: AbilityDef): void {
    if (this.localState?.isPlayingEvents) return;

    const combatData = StateManager.get('combatData');
    if (!combatData) return;

    // Block if active combatant can't afford the focus cost
    const active = combatData.combatants.find(c => c.id === combatData.activeCombatantId);
    if (active) {
      const skill = active.skills?.find(g => g.abilityId === ability.id);
      const focusCost = skill?.focusCost ?? 0;
      if (active.focus < focusCost) return;
    }

    // Re-clicking the same ability = confirm on first valid target
    if (this.pendingAbility?.id === ability.id) {
      const isAllyTarget = ability.targeting === 'ally_single' || ability.targeting === 'ally_all';
      const side = isAllyTarget ? 'player' : 'enemy';
      const alive = combatData.combatants.filter(c => c.side === side && c.isAlive);
      if (alive.length === 0) {
        this.clearTargetingMode();
        return;
      }
      const confirmedAbility = this.pendingAbility;
      this.clearTargetingMode();
      this.submitAbility(confirmedAbility, alive[0].id);
      return;
    }

    // Clear any existing targeting
    if (this.pendingAbility) {
      this.clearTargetingMode();
    }

    // Self abilities: submit immediately
    if (ability.targeting === 'self') {
      this.submitAbility(ability, combatData.activeCombatantId);
      return;
    }

    // Ally targeting: highlight party green
    if (ability.targeting === 'ally_single' || ability.targeting === 'ally_all') {
      const allies = combatData.combatants.filter(c => c.side === 'player' && c.isAlive);
      if (allies.length === 0) return;

      const mode = ability.targeting === 'ally_all' ? 'all' : 'single';
      this.pendingAbility = ability;
      EventBus.emit('COMBAT_HIGHLIGHT_TARGETS', { ids: allies.map(a => a.id), mode, side: 'player' });
      this.combatHUD?.showTargetPicker(
        allies.map(a => ({ id: a.id, name: a.name, spriteKey: a.spriteKey })),
        mode,
        'player',
      );
      this.combatHUD?.setTargetingMode(true, mode, 'player');
      this.showTimelinePreview(ability);
      this.onRender();
      return;
    }

    // Enemy targeting: highlight enemies blue/red. Mirror the engine's vanish-fallback pattern
    // (enemyAI.ts:130) — prefer non-vanished enemies, fall through to vanished if none visible
    // so the player never gets a no-targets dead end.
    const alive = combatData.combatants.filter(c => c.side === 'enemy' && c.isAlive);
    if (alive.length === 0) return;
    const visible = alive.filter(c => !c.statusEffects?.some(e => e.type === 'vanish'));
    const pool = visible.length > 0 ? visible : alive;

    const mode = ability.targeting === 'all' ? 'all' : 'single';
    this.pendingAbility = ability;
    EventBus.emit('COMBAT_HIGHLIGHT_TARGETS', { ids: pool.map(e => e.id), mode, side: 'enemy' });
    this.combatHUD?.showTargetPicker(
      pool.map(e => ({ id: e.id, name: e.name, spriteKey: e.spriteKey })),
      mode,
    );
    this.combatHUD?.setTargetingMode(true, mode, 'enemy');
    this.showTimelinePreview(ability);
    this.onRender();
  }

  private onTargetSelected(targetId: string): void {
    if (!this.pendingAbility || !this.localState) return;
    const ability = this.pendingAbility;
    this.clearTargetingMode();
    this.submitAbility(ability, targetId);
  }

  private onTargetHovered(targetId: string | null): void {
    if (!this.pendingAbility) return;
    // Determine which side we're targeting based on the ability
    const isAlly = this.pendingAbility.targeting === 'ally_single' || this.pendingAbility.targeting === 'ally_all';
    const side: 'enemy' | 'player' = isAlly ? 'player' : 'enemy';
    // Highlight the hovered target's portrait in the timeline
    this.speedTimeline?.setTargeted(targetId, side);
    // Re-compute timeline preview with the hovered target's stagger effect
    this.showTimelinePreview(this.pendingAbility, targetId ?? undefined);
  }

  private clearTargetingMode(): void {
    this.pendingAbility = null;
    this.combatHUD?.hideTargetPicker();
    this.combatHUD?.setTargetingMode(false);
    this.combatHUD?.setTarget(null);
    this.speedTimeline?.setTargeted(null);
    EventBus.emit('COMBAT_CLEAR_HIGHLIGHTS');
    this.clearTimelinePreview();
    this.onRender();
  }

  private onSkipTurn(): void {
    if (this.localState?.isPlayingEvents) return;
    this.submitAction({ type: 'skip' });
  }

  private async submitAbility(ability: AbilityDef, targetId: string): Promise<void> {
    // Optimistically update focus bar immediately (before server responds)
    const combatData = StateManager.get('combatData');
    if (combatData && this.activeMaxFocus > 0) {
      const active = combatData.combatants.find(c => c.id === combatData.activeCombatantId);
      if (active) {
        const skill = active.skills?.find(g => g.abilityId === ability.id);
        const focusCost = skill?.focusCost ?? 0;
        if (focusCost > 0) {
          const newFocus = Math.min(this.activeMaxFocus, Math.max(0, active.focus - focusCost));
          const pct = (newFocus / this.activeMaxFocus) * 100;
          const focusFill = document.getElementById('active-focus-fill');
          const focusText = document.getElementById('active-focus-text');
          if (focusFill) focusFill.style.width = `${pct}%`;
          if (focusText) focusText.textContent = `${newFocus}/${this.activeMaxFocus}`;
        }
      }
    }

    await this.submitAction({
      type: 'skill',
      abilityId: ability.id,
      targetId,
    });
  }

  private async submitAction(action: CombatAction): Promise<void> {
    if (!this.localState) return;

    this.clearTimelinePreview();
    this.localState.isPlayingEvents = true;
    this.setButtonsEnabled(false);

    const result = await APIManager.submitAction(action);

    if (!result.success || !result.data) {
      console.error('[CombatScreen] Action failed:', result.error);
      this.updateActionLog(`Error: ${result.error ?? 'Action failed'}`);
      this.localState.isPlayingEvents = false;
      this.onRender();
      return;
    }

    const { combatState, events } = result.data;

    // Feed events into summary tracker + play with animations
    CombatSummaryTracker.ingestEvents(events, combatState);
    await EventPlaybackManager.playEvents(events, combatState);

    // Update state after playback
    StateManager.set('combatData', combatState);

    // Sync Phaser's active turn glow with the server's active combatant
    // (the next player's turn_start may not be in the playback events)
    EventBus.emit(Events.ACTIVE_TURN_CHANGED, combatState.activeCombatantId);

    this.localState.isPlayingEvents = false;

    // Check final status
    if (combatState.status === 'victory') {
      this.localState.phase = 'victory';
    } else if (combatState.status === 'defeat') {
      this.localState.phase = 'defeat';
    } else if (combatState.status === 'fled') {
      ScreenRouter.replace('main-menu');
      return;
    }

    this.onRender();
  }

  // ---------------------------------------------------------------------------
  // Action panel restore
  // ---------------------------------------------------------------------------

  private restoreActionPanel(): void {
    if (!this.elements.actionPanel) return;

    this.celebrationStarted = false;
    this.victoryBannerShown = false;
    this.pendingRewards = null;
    this.elements.actionPanel.style.display = '';
    this.elements.actionPanel.classList.remove('result-mode');
    this.elements.actionPanel.innerHTML = `
      <div id="active-char-portrait" class="active-char-portrait-wrap"></div>
      <div id="active-character-info">
        <div class="active-char-details">
          <span id="active-char-name">...</span>
          <span id="active-char-class"></span>
        </div>
        <div id="active-focus-row" style="display:none">
          <div id="active-focus-bar"><div id="active-focus-fill"></div></div>
          <span id="active-focus-text"></span>
          <span id="fervor-pips" class="fervor-pips-inline" style="display:none"></span>
        </div>
      </div>
      <div id="ability-buttons"></div>
    `;

    // Re-cache elements
    this.elements.activeCharName = document.getElementById('active-char-name');
    this.elements.activeCharClass = document.getElementById('active-char-class');
    this.elements.activeCharPortrait = document.getElementById('active-char-portrait');
    this.elements.abilityButtons = document.getElementById('ability-buttons');
    this.clearActionLog();
  }

  // ---------------------------------------------------------------------------
  // Victory overlay
  // ---------------------------------------------------------------------------

  private renderVictory(): void {
    if (!this.elements.actionPanel) return;
    if (this.victoryBannerShown) return;
    this.victoryBannerShown = true;

    // Keep action panel visible but dim/disable the buttons
    this.elements.actionPanel.classList.add('victory-idle');

    // Use rewards from playback event (captured early) or fall back to StateManager
    const combatData = StateManager.get('combatData') as ServerCombatState | undefined;
    const rewards = this.pendingRewards ?? combatData?.rewards;
    const levelUps = combatData?.unitLevelUps;

    // Player-side party combatants (exclude summons — they don't gain XP).
    const partyCombatants = (combatData?.combatants ?? [])
      .filter(c => c.side === 'player' && !c.isSummoned)
      .sort((a, b) => a.slotIndex - b.slotIndex);

    // Stagger XP floaters across the party so they land like a quick cascade
    // rather than a synchronized blob. ~110ms feels like a heartbeat per strand.
    if (rewards?.xp && this.floatingText) {
      partyCombatants.forEach((c, i) => {
        setTimeout(() => {
          this.floatingText?.show(c.id, `+${rewards.xp} XP`, 'xp');
        }, i * 110);
      });
    }

    // Gold attaches to the active combatant — whoever finished the kill.
    // No persistent wallet; the float is the whole feedback.
    if (rewards?.gold && this.floatingText) {
      const activeId = combatData?.activeCombatantId
        ?? partyCombatants[0]?.id;
      if (activeId) {
        // Slight x-offset and y-offset so the gold floater doesn't stack on top
        // of the active strand's XP floater when they happen to be the same unit.
        this.floatingText.show(activeId, `+${formatGold(rewards.gold)} GOLD`, 'gold', -28, 0);
      }
    }

    // Level-up floaters fire AFTER the XP floaters have had a beat to read,
    // and on the specific strand that leveled (matched by unitId).
    if (levelUps && levelUps.length > 0 && this.floatingText) {
      levelUps.forEach((lu, i) => {
        const target = partyCombatants.find(c => c.unitId === lu.unitId);
        if (!target) return;
        setTimeout(() => {
          this.floatingText?.show(target.id, 'LEVEL UP!', 'levelup', -36);
        }, 850 + i * 220);
      });
    }

    // Auto-advance once the floaters have played out. Longer hold when something
    // leveled so the celebratory pop has room to breathe. The end-of-crawl
    // sentiment prompt fires from advanceCrawl() on the crawl-complete branch.
    const hasLevelUp = levelUps && levelUps.length > 0;
    const advanceDelayMs = hasLevelUp ? 2400 : 1600;

    setTimeout(() => {
      this.pendingRewards = null;
      this.elements.actionPanel?.classList.remove('victory-idle');
      this.advanceCrawl();
    }, advanceDelayMs);
  }

  // ---------------------------------------------------------------------------
  // Beta sentiment prompt (Easy / Normal / Hard) — end of crawl only
  // ---------------------------------------------------------------------------

  private showSentimentPrompt(encounterId: string | null, onDone: () => void): void {
    const panel = this.elements.actionPanel;
    if (!panel) {
      onDone();
      return;
    }

    panel.classList.add('result-mode');
    panel.innerHTML = `
      <div class="combat-result-overlay sentiment-prompt">
        <h2>How was that crawl?</h2>
        <div class="sentiment-buttons">
          <button class="sentiment-btn sentiment-easy" data-rating="easy">Too Easy</button>
          <button class="sentiment-btn sentiment-normal" data-rating="normal">Just Right</button>
          <button class="sentiment-btn sentiment-hard" data-rating="hard">Too Hard</button>
        </div>
        <button class="sentiment-skip">Skip</button>
      </div>
    `;

    const finish = (rating: 'easy' | 'normal' | 'hard' | null) => {
      if (rating && encounterId) reportFightSentiment(encounterId, rating);
      panel.classList.remove('result-mode');
      panel.innerHTML = '';
      onDone();
    };

    panel.querySelectorAll('.sentiment-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const rating = (btn as HTMLElement).dataset.rating as 'easy' | 'normal' | 'hard' | undefined;
        panel.querySelectorAll('button').forEach(b => (b as HTMLButtonElement).disabled = true);
        finish(rating ?? null);
      });
    });
    panel.querySelector('.sentiment-skip')?.addEventListener('click', () => {
      panel.querySelectorAll('button').forEach(b => (b as HTMLButtonElement).disabled = true);
      finish(null);
    });
  }

  private async advanceCrawl(): Promise<void> {
    const btn = this.elements.actionPanel?.querySelector('.continue-btn') as HTMLButtonElement;
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Advancing...';
    }

    // Snapshot discovered strands BEFORE the advance — so renderCrawlComplete
    // can detect first-time strand drops reliably, regardless of any state
    // refresh that happens after this call.
    const playerSnap = StateManager.get('playerData') as { discoveredStrands?: string } | undefined;
    this.preAdvanceDiscoveredStrands = parseDiscoveredStrands(playerSnap);

    const result = await APIManager.advanceCrawl();

    if (!result.success || !result.data) {
      console.error('[CombatScreen] Failed to advance crawl:', result.error);
      // Restore controls so the user isn't stuck
      this.restoreActionPanel();
      this.localState!.phase = 'combat';
      this.onRender();
      this.updateActionLog(`Error: ${result.error ?? 'Failed to advance'}`);
      return;
    }

    const { run, combatResult, unitDrop, itemDrop, unitLevelUps, extraItemDrops, currencyDrops } = result.data as any;

    // Update crawl data
    const crawlData = StateManager.get('crawlData') as CrawlData | undefined;
    if (crawlData) {
      StateManager.set('crawlData', { ...crawlData, run });
    }

    // Fork choice pending — show path selection UI
    if (run.pendingFork) {
      this.pendingForkPaths = run.pendingFork.paths;
      this.restoreActionPanel();
      this.localState!.phase = 'fork_choice';
      this.onRender();
      return;
    }

    if (combatResult) {
      // Next encounter
      this.restoreActionPanel();
      this.localState!.phase = 'combat';

      const sceneData = this.buildSceneData(combatResult.combatState);
      this.activeCombatId = combatResult.combatState.id;

      if (this.phaserInitialized) {
        // Room transition — keep players, swap enemies in Phaser
        EventBus.emit(Events.COMBAT_ROOM_TRANSITION, sceneData);
        this.showCombatUI();

        // Update HUD in-place: remove enemy bars, add new enemies, keep player bars
        const newCombatants = combatResult.combatState.combatants
          .filter((c: any) => c.isAlive)
          .map((c: any) => ({
            id: c.id, name: c.name, current: c.hp, max: c.maxHp,
            shield: sumCombatantBarrier(c), barriers: c.barriers ?? [], isEnemy: c.side === 'enemy',
            elements: (c.defensiveTypes && c.defensiveTypes.length > 0) ? c.defensiveTypes : (c.element ? [c.element] : undefined),
          statusEffects: c.statusEffects,
            tempo: c.tempo, focus: c.focus, maxFocus: c.maxFocus,
          }));
        this.combatHUD?.transitionEnemies(newCombatants);
        this.updateTimeline(combatResult.combatState);
      } else {
        this.startOrRestartScene(sceneData);
      }

      await this.playInitialEvents(combatResult);
    } else {
      // Crawl complete!
      this.pendingUnitDrop = unitDrop ?? null;
      this.pendingItemDrop = itemDrop ?? null;
      this.pendingExtraItemDrops = extraItemDrops ?? [];
      this.pendingCurrencyDrops = currencyDrops ?? [];
      this.pendingSkinDrop = (result.data as any).skinDrop ?? null;
      this.pendingUnitLevelUps = unitLevelUps ?? null;

      // Grab the final encounter id BEFORE we clear combatData so we can
      // attribute the end-of-crawl sentiment to its boss/last fight.
      const finalCombatData = StateManager.get('combatData') as ServerCombatState | undefined;
      const finalEncounterId = finalCombatData?.encounterId ?? null;
      StateManager.set('combatData', null);

      this.showSentimentPrompt(finalEncounterId, () => {
        this.localState!.phase = 'crawl_complete';
        this.onRender();
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Fork choice overlay
  // ---------------------------------------------------------------------------

  private renderForkChoice(): void {
    if (!this.elements.actionPanel || !this.pendingForkPaths) return;

    const paths = this.pendingForkPaths;
    const pathEntries = Object.entries(paths);

    const pathCards = pathEntries.map(([pathId, path]) => {
      const textChips = (path.hints ?? []).map(h =>
        `<span class="fork-hint-chip">${h}</span>`
      ).join('');
      const elementChips = (path.elements ?? []).map(e =>
        `<span class="fork-hint-chip fork-hint-chip--element" style="color:${elementColor(e)}" data-element="${e}">` +
          `<img class="fork-hint-icon" src="${getElementIconUrl(e)}" alt="">` +
          `${formatElement(e)}` +
        `</span>`
      ).join('');
      const allChips = textChips + elementChips;
      const hintRow = allChips ? `<div class="fork-path-hints">${allChips}</div>` : '';
      return `
      <button class="fork-path-card" data-path-id="${pathId}" type="button">
        <h3 class="fork-path-label">${path.label}</h3>
        ${hintRow}
        <p class="fork-path-desc">${path.description}</p>
      </button>
    `;
    }).join('');

    this.elements.actionPanel.classList.add('result-mode');
    this.elements.actionPanel.innerHTML = `
      <div class="combat-result-overlay fork-choice">
        <h2>Choose Your Path</h2>
        <div class="fork-paths">${pathCards}</div>
      </div>
    `;

    // Bind weak/resists tooltip on element chips (matches CombatHUD enemy element icons)
    this.elements.actionPanel.querySelectorAll('.fork-hint-chip--element').forEach(chip => {
      const element = (chip as HTMLElement).dataset.element;
      if (element) Tooltip.attach(chip as HTMLElement, () => buildElementTooltip(element));
    });

    // Bind click handlers
    const buttons = this.elements.actionPanel.querySelectorAll('.fork-path-card');
    buttons.forEach((btn) => {
      btn.addEventListener('click', async () => {
        const pathId = (btn as HTMLElement).dataset.pathId;
        if (!pathId) return;

        // Disable all buttons while loading
        buttons.forEach(b => {
          (b as HTMLButtonElement).disabled = true;
          b.classList.add('loading');
        });

        const result = await APIManager.chooseFork(pathId);
        if (!result.success || !result.data) {
          console.error('[CombatScreen] Failed to choose fork:', result.error);
          buttons.forEach(b => {
            (b as HTMLButtonElement).disabled = false;
            b.classList.remove('loading');
          });
          return;
        }

        this.pendingForkPaths = null;

        const { run, combatResult } = result.data as any;

        // Update crawl data
        const crawlData = StateManager.get('crawlData') as CrawlData | undefined;
        if (crawlData) {
          StateManager.set('crawlData', { ...crawlData, run });
        }

        // Transition to combat
        this.restoreActionPanel();
        this.localState!.phase = 'combat';

        const sceneData = this.buildSceneData(combatResult.combatState);
        this.activeCombatId = combatResult.combatState.id;

        if (this.phaserInitialized) {
          EventBus.emit(Events.COMBAT_ROOM_TRANSITION, sceneData);
          this.showCombatUI();
          const newCombatants = combatResult.combatState.combatants
            .filter((c: any) => c.isAlive)
            .map((c: any) => ({
              id: c.id, name: c.name, current: c.hp, max: c.maxHp,
              shield: sumCombatantBarrier(c), barriers: c.barriers ?? [], isEnemy: c.side === 'enemy',
              elements: (c.defensiveTypes && c.defensiveTypes.length > 0) ? c.defensiveTypes : (c.element ? [c.element] : undefined),
          statusEffects: c.statusEffects,
              tempo: c.tempo, focus: c.focus, maxFocus: c.maxFocus,
            }));
          this.combatHUD?.transitionEnemies(newCombatants);
          this.updateTimeline(combatResult.combatState);
        } else {
          this.startOrRestartScene(sceneData);
        }

        await this.playInitialEvents(combatResult);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Defeat overlay
  // ---------------------------------------------------------------------------

  private openCombatMenu(): void {
    if (document.querySelector('.combat-menu-overlay')) return;

    const overlay = document.createElement('div');
    overlay.className = 'combat-menu-overlay';
    overlay.innerHTML = `
      <div class="combat-menu-panel">
        <div class="combat-menu-header">
          <span class="combat-menu-title">Menu</span>
          <button class="combat-menu-close">&times;</button>
        </div>
        <div class="combat-menu-options">
          <button class="combat-menu-option abandon-option">
            <span class="combat-menu-option-label">Retreat from Crawl</span>
            <span class="combat-menu-option-desc">Abandon this run. Completion rewards are forfeited.</span>
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    overlay.querySelector('.combat-menu-close')?.addEventListener('click', () => overlay.remove());

    overlay.querySelector('.abandon-option')?.addEventListener('click', () => {
      // Replace menu content with confirmation
      const panel = overlay.querySelector('.combat-menu-panel')!;
      panel.innerHTML = `
        <div class="combat-menu-header">
          <span class="combat-menu-title">Retreat?</span>
        </div>
        <p class="combat-menu-confirm-text">Completion rewards will be forfeited.</p>
        <div class="combat-menu-confirm-btns">
          <button class="combat-btn abandon-yes">Retreat</button>
          <button class="combat-btn abandon-no">Cancel</button>
        </div>
      `;
      panel.querySelector('.abandon-no')?.addEventListener('click', () => overlay.remove());
      panel.querySelector('.abandon-yes')?.addEventListener('click', async () => {
        overlay.remove();
        await APIManager.abandonCrawl();
        StateManager.set('combatData', null);
        ScreenRouter.replace('main-menu');
      });
    });

  }

  private renderDefeat(): void {
    if (!this.elements.actionPanel) return;

    // Build cause-of-death report
    const deaths = CombatSummaryTracker.getDeathReport();
    let deathHTML = '';
    if (deaths.length > 0) {
      const rows = deaths.map(d => {
        const cod = d.causeOfDeath!;
        const elColor = cod.element ? elementColor(cod.element) : 'var(--color-text-secondary)';
        const elLabel = cod.element ? ` ${cod.element}` : '';
        return `
          <div class="cs-death-row">
            <span class="cs-death-name">${d.name}</span>
            <span class="cs-death-cause">slain by <strong>${cod.sourceName}</strong></span>
            <span class="cs-death-detail" style="color:${elColor}">${cod.abilityName} — ${cod.damage.toLocaleString()}${elLabel}</span>
          </div>
        `;
      }).join('');
      deathHTML = `
        <div class="cs-death-section">
          <div class="cs-section-label">CAUSE OF DEATH</div>
          ${rows}
        </div>
      `;
    }

    // Combat stats
    const statsHTML = this.renderCombatStatsTab();

    // Hide the action panel — defeat uses a full-screen scrim overlay instead
    this.elements.actionPanel.style.display = 'none';

    // Remove any existing defeat overlay (idempotent re-render)
    document.getElementById('defeat-overlay')?.remove();

    const overlay = document.createElement('div');
    overlay.id = 'defeat-overlay';
    overlay.className = 'defeat-scrim';
    overlay.innerHTML = `
      <div class="defeat-modal">
        <div class="defeat-modal-scroll">
          <h2 class="cs-defeat-title">Defeat</h2>
          <p class="cs-defeat-sub">Your party has fallen...</p>
          ${deathHTML}
          <div class="cs-defeat-stats">${statsHTML}</div>
        </div>
        <button class="combat-btn return-btn" type="button">Return to Menu</button>
      </div>
    `;
    document.getElementById('battle-container')!.appendChild(overlay);

    overlay.querySelector('.return-btn')?.addEventListener('click', () => {
      overlay.remove();
      // Crawl is already marked 'defeated' server-side by finalizeCombat,
      // so no need to call abandonCrawl here — just navigate away.
      StateManager.set('combatData', null);
      StateManager.set('crawlData', null);
      ScreenRouter.replace('main-menu');
    });
  }

  // ---------------------------------------------------------------------------
  // Crawl celebration + complete overlay
  // ---------------------------------------------------------------------------

  private startCrawlCelebration(): void {
    if (this.celebrationStarted) return;
    this.celebrationStarted = true;

    if (this.elements.actionPanel) {
      this.elements.actionPanel.style.display = 'none';
    }

    let resolved = false;
    const finish = async () => {
      if (resolved) return;
      resolved = true;
      // Campaign first-clear dialogue plays BEFORE the loot card so the
      // narrative beat lands before the mechanical payoff. Dailies/replays
      // skip this and go straight to loot.
      await this.playCampaignDebrief();
      this.renderCrawlComplete();
    };

    // Trigger Phaser particle celebration
    EventBus.emit(Events.COMBAT_CRAWL_CELEBRATE);

    // When celebration finishes, show loot card
    EventBus.once(Events.CRAWL_CELEBRATE_DONE, finish);

    // Safety timeout — if Phaser never fires the event, show loot card anyway
    setTimeout(finish, 3000);
  }

  /**
   * Play the campaign decision/debrief dialogue (if any) over the combat
   * backdrop. No-op for non-campaign crawls, replay runs, or chapters
   * without a decision or debriefDialogue configured.
   */
  private async playCampaignDebrief(): Promise<void> {
    const cData = StateManager.get('crawlData') as CrawlData | undefined;
    const campData = StateManager.get('campaignData') as { campaignStep: number; steps: { crawlId: string }[] } | undefined;
    if (!cData?.run?.crawlConfigId || !campData) return;

    const stepIdx = campData.steps.findIndex(s => s.crawlId === cData.run!.crawlConfigId);
    if (stepIdx < 0 || campData.campaignStep !== stepIdx) return;

    const briefingData = CHAPTER_BRIEFINGS[stepIdx];
    if (!briefingData?.decision && !briefingData?.debriefDialogue) return;

    // Hide combat chrome + sprites so the dialogue owns the screen.
    if (this.elements.combatHud) this.elements.combatHud.style.display = 'none';
    const floatingHud = document.getElementById('floating-hud');
    if (floatingHud) floatingHud.style.display = 'none';
    EventBus.emit('COMBAT_HIDE_SPRITES');

    if (briefingData.decision && cData.run.crawlConfigId) {
      await showDecisionScene(briefingData.decision, cData.run.crawlConfigId);
    }
    if (briefingData.debriefDialogue) {
      await showDebriefDialogue(briefingData.debriefDialogue, stepIdx);
    }

    // Restore the combat HUD shell so renderCrawlComplete has a host for the
    // loot card. Sprites AND floating-hud (HP bars + combat text) stay hidden —
    // the loot card is a results summary; the combat backdrop/HP bars are no
    // longer part of it and were bleeding through the strand reveal card.
    if (this.elements.combatHud) this.elements.combatHud.style.display = '';
  }

  private renderCrawlComplete(): void {
    if (!this.elements.actionPanel) return;

    // Hide the combat backdrop — floating HP bars and combat text would otherwise
    // bleed through the strand-reveal / loot card. The card is a summary screen;
    // no combat UI belongs behind it.
    const floatingHud = document.getElementById('floating-hud');
    if (floatingHud) floatingHud.style.display = 'none';
    EventBus.emit('COMBAT_HIDE_SPRITES');

    const crawlData = StateManager.get('crawlData') as CrawlData | undefined;
    const bonus = crawlData?.config.completionBonus;
    const unitDrop = this.pendingUnitDrop;
    const itemDropData = this.pendingItemDrop;
    const levelUps = this.pendingUnitLevelUps;

    // Check if this is a first-time strand discovery
    const isNewStrand = !!(unitDrop && !this.preAdvanceDiscoveredStrands.has(unitDrop.unitClass));

    // Inline reward badges
    const bonusBadges: string[] = [];
    if (bonus?.xp) bonusBadges.push(`<span class="cc-badge cc-badge--xp">+${bonus.xp} XP</span>`);
    if (bonus?.gold) bonusBadges.push(`<span class="cc-badge cc-badge--gold">+${bonus.gold} Gold</span>`);

    // Level-up pills (no MP — irrelevant to the player here)
    const levelUpPills = (levelUps && levelUps.length > 0)
      ? `<div class="cc-levelups">${levelUps.map(lu =>
          `<span class="cc-levelup-pill">${lu.unitName} → Lv.${lu.newLevel}</span>`
        ).join('')}</div>`
      : '';

    // Currency badges
    const currencyBadges = this.renderCurrencyBadges();

    // Crawl type for share button — daily crawls have configId starting with "daily_"
    const isDaily = crawlData?.run?.crawlConfigId?.startsWith('daily_') ?? false;

    // Combat stats tab content
    const statsHTML = this.renderCombatStatsTab();

    // Build strand reveal page (page 1) if this is a new discovery
    const strandRevealHTML = isNewStrand ? this.renderStrandRevealPage(unitDrop!) : '';

    this.elements.actionPanel.style.display = '';
    this.elements.actionPanel.classList.add('result-mode');
    this.elements.actionPanel.innerHTML = `
      <div class="combat-result-overlay cc-overlay">
        ${strandRevealHTML ? `
        <div class="cc-page cc-page-strand">
          ${strandRevealHTML}
        </div>
        ` : ''}
        <div class="cc-page cc-page-loot${strandRevealHTML ? ' hidden' : ''}">
          <div class="cc-header">
            <div class="cc-header-row">
              <span class="cc-title">Crawl Complete</span>
              ${bonusBadges.join('')}
            </div>
            ${levelUpPills}
          </div>
          <div class="cc-tabs">
            <button class="cc-tab cc-tab--active" data-tab="rewards" type="button">Rewards</button>
            <button class="cc-tab" data-tab="stats" type="button">Stats</button>
          </div>
          <div class="cc-tab-content cc-tab-rewards">
            <div class="cc-drops">
              ${this.renderDropsCascade(unitDrop, itemDropData)}
            </div>
            ${currencyBadges ? `<div class="cc-currencies">${currencyBadges}</div>` : ''}
          </div>
          <div class="cc-tab-content cc-tab-stats hidden">
            ${statsHTML}
          </div>
          <div class="cc-footer">
            ${isDaily ? shareButtonHTML('daily', undefined, 'Share Daily') : ''}
            <button class="combat-btn done-btn" type="button">Done</button>
          </div>
        </div>
      </div>
    `;

    // Wire strand reveal → loot page transition
    if (strandRevealHTML) {
      const strandPage = this.elements.actionPanel.querySelector('.cc-page-strand');
      const lootPage = this.elements.actionPanel.querySelector('.cc-page-loot');
      const continueBtn = this.elements.actionPanel.querySelector('[data-sd-action="continue"]');
      continueBtn?.addEventListener('click', () => {
        if (strandPage && lootPage) {
          strandPage.classList.add('cc-page-out');
          setTimeout(() => {
            strandPage.classList.add('hidden');
            lootPage.classList.remove('hidden');
            lootPage.classList.add('cc-page-in');
          }, 200);
        }
      });
    }

    // Tab switching
    const tabs = this.elements.actionPanel.querySelectorAll('.cc-tab');
    const rewardsPane = this.elements.actionPanel.querySelector('.cc-tab-rewards');
    const statsPane = this.elements.actionPanel.querySelector('.cc-tab-stats');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('cc-tab--active'));
        tab.classList.add('cc-tab--active');
        const target = (tab as HTMLElement).dataset.tab;
        if (target === 'rewards') {
          rewardsPane?.classList.remove('hidden');
          statsPane?.classList.add('hidden');
        } else {
          rewardsPane?.classList.add('hidden');
          statsPane?.classList.remove('hidden');
        }
      });
    });

    const btn = this.elements.actionPanel.querySelector('.done-btn');
    btn?.addEventListener('click', () => {
      this.onCrawlDone();
    });
    bindShareButtons(this.elements.actionPanel);
  }

  /**
   * Render the strand reveal "page 1" content for the crawl complete card.
   * Shows the big portrait, name, type/stats — lives inside the same card as loot.
   */
  private renderStrandRevealPage(unitDrop: UnitDropData): string {
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const template = configData?.unitTemplates?.[unitDrop.unitClass];
    const spriteKey = template?.spriteKey ?? unitDrop.unitClass;
    const spriteSrc = getFullSpriteUrl(spriteKey) || getPortraitUrl(spriteKey);
    const strandName = template?.name ?? formatUnitClass(unitDrop.unitClass);
    const race = formatRace(unitDrop.race);
    const defTypes = formatDefensiveTypes(template?.defensiveTypes ?? []);
    const metaLine = defTypes ? `${race} \u00b7 ${defTypes}` : race;
    const pullQuote = template?.pullQuote?.trim();

    return `
      <div class="cc-strand-kicker">New Strand Discovered</div>
      <div class="cc-strand-hero">
        <div class="cc-strand-glow"></div>
        ${spriteSrc ? `<img src="${spriteSrc}" alt="${strandName}" draggable="false" />` : ''}
      </div>
      <div class="cc-strand-name">${strandName}</div>
      <div class="cc-strand-meta">${metaLine}</div>
      ${pullQuote ? `<div class="cc-strand-quote">\u201C${pullQuote}\u201D</div>` : ''}
      <button type="button" class="sd-btn sd-btn-primary" data-sd-action="continue">Continue</button>
    `;
  }

  private renderCombatStatsTab(): string {
    const summary = CombatSummaryTracker.getSummary();
    const players = CombatSummaryTracker.getPlayerSummaries();

    if (players.length === 0) {
      return '<div class="cs-empty">No combat data recorded.</div>';
    }

    const overviewHTML = `
      <div class="cs-overview">
        <span class="cs-overview-stat">${summary.totalTurns} turns</span>
        <span class="cs-overview-sep">&middot;</span>
        <span class="cs-overview-stat">${summary.encountersCompleted} encounter${summary.encountersCompleted !== 1 ? 's' : ''}</span>
      </div>
    `;

    const strandCards = players.map(p => {
      // Build element damage bars
      const elements = Object.entries(p.damageByElement)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1]);

      const maxDmg = elements.length > 0 ? elements[0][1] : 1;

      const barsHTML = elements.map(([el, dmg]) => {
        const pct = Math.max(5, Math.round((dmg / maxDmg) * 100));
        const color = elementColor(el);
        return `
          <div class="cs-bar-row">
            <div class="cs-bar-fill" style="width:${pct}%;background:${color}"></div>
            <span class="cs-bar-label">${el}</span>
            <span class="cs-bar-value">${dmg.toLocaleString()}</span>
          </div>
        `;
      }).join('');

      // Stat pills
      const pills: string[] = [];
      if (p.critsLanded > 0) pills.push(`<span class="cs-pill cs-pill--crit">${p.critsLanded} Crit${p.critsLanded !== 1 ? 's' : ''}</span>`);
      if (p.weaknessHits > 0) pills.push(`<span class="cs-pill cs-pill--weak">${p.weaknessHits} Weakness</span>`);
      if (p.resistedHits > 0) pills.push(`<span class="cs-pill cs-pill--resist">${p.resistedHits} Resisted</span>`);

      const healRow = p.totalHealingDone > 0
        ? `<div class="cs-stat-row"><span class="cs-stat-label">HEALING</span><span class="cs-stat-value cs-heal">${p.totalHealingDone.toLocaleString()}</span></div>`
        : '';

      const spriteUrl = `/activity/sprites/processed/players/${p.spriteKey}_sm.webp${CB}`;

      return `
        <div class="cs-strand-card">
          <div class="cs-strand-header">
            <img class="cs-strand-portrait" src="${spriteUrl}" alt="${p.name}" />
            <div class="cs-strand-info">
              <span class="cs-strand-name">${p.name}</span>
            </div>
          </div>
          <div class="cs-stat-row">
            <span class="cs-stat-label">DMG DEALT</span>
            <span class="cs-stat-value">${p.totalDamageDealt.toLocaleString()}</span>
          </div>
          ${barsHTML ? `<div class="cs-bars">${barsHTML}</div>` : ''}
          <div class="cs-stat-row">
            <span class="cs-stat-label">DMG TAKEN</span>
            <span class="cs-stat-value">${p.totalDamageTaken.toLocaleString()}</span>
          </div>
          ${healRow}
          ${pills.length > 0 ? `<div class="cs-pills">${pills.join('')}</div>` : ''}
        </div>
      `;
    }).join('');

    return `${overviewHTML}${strandCards}`;
  }

  private renderCurrencyBadges(): string {
    if (this.pendingCurrencyDrops.length === 0) return '';
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const currencies = configData?.currencies;
    if (!currencies) return '';

    return this.pendingCurrencyDrops.map(drop => {
      const def: CurrencyDef | undefined = currencies[drop.currencyId];
      if (!def) return '';
      const label = drop.amount > 1 ? `${def.name} ×${drop.amount}` : def.name;
      return `<span class="cc-currency-badge" style="color:${def.color}">${renderCurrencyIcon(def.id, 18)} ${label}</span>`;
    }).join('');
  }

  /** Collect all drops into a cascade-indexed sequence for staggered animation */
  private renderDropsCascade(
    unitDrop: UnitDropData | null,
    itemDrop: ItemData | null,
  ): string {
    const rows: string[] = [];
    let i = 0;
    if (unitDrop) rows.push(this.renderUnitDrop(unitDrop, i++));
    if (itemDrop) rows.push(this.renderItemDrop(itemDrop, i++));
    for (const d of this.pendingExtraItemDrops) rows.push(this.renderItemDrop(d, i++));
    if (this.pendingSkinDrop) rows.push(this.renderSkinDrop(this.pendingSkinDrop, i++));
    return rows.join('');
  }

  private renderUnitDrop(drop: UnitDropData, idx = 0): string {
    const rColor = getRarityColor(drop.rarity);
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const template = configData?.unitTemplates?.[drop.unitClass];
    const spriteKey = template?.spriteKey ?? drop.unitClass;
    const portraitSrc = `/activity/sprites/processed/players/${spriteKey}_sm.webp${CB}`;
    const className = template?.name ?? formatUnitClass(drop.unitClass);
    const defTypes = formatDefensiveTypes(template?.defensiveTypes ?? []);
    const metaSub = defTypes ? `${className} · ${formatRace(drop.race)} · ${defTypes}` : `${className} · ${formatRace(drop.race)}`;

    const affixSummary = this.parseAffixSummary(drop.explicitAffixes, configData);

    return `
      <div class="cc-drop-row cc-drop-row--strand" style="--drop-i:${idx}; --rarity-color:${rColor}">
        <div class="cc-drop-portrait"><img src="${portraitSrc}" alt="${className}" /></div>
        <div class="cc-drop-body">
          <div class="cc-drop-name" style="color: ${rColor}">${drop.name}</div>
          <div class="cc-drop-sub">${metaSub}</div>
          ${affixSummary ? `<div class="cc-drop-affixes">${affixSummary}</div>` : ''}
        </div>
      </div>
    `;
  }

  private renderItemDrop(item: ItemData, idx = 0): string {
    const rColor = getRarityColor(item.rarity ?? 'normal');
    const categoryLabel = formatCategory(item.category);
    const configData = StateManager.get('configData') as ConfigData | undefined;

    const affixSummary = this.parseItemAffixSummary(item, configData);

    return `
      <div class="cc-drop-row" style="--drop-i:${idx}; --rarity-color:${rColor}">
        <div class="cc-drop-icon">${renderItemIcon(item.baseType ?? '', item.category, 'md')}</div>
        <div class="cc-drop-body">
          <div class="cc-drop-name" style="color: ${rColor}">${item.name}</div>
          <div class="cc-drop-sub">${categoryLabel}</div>
          ${affixSummary ? `<div class="cc-drop-affixes">${affixSummary}</div>` : ''}
        </div>
      </div>
    `;
  }

  private renderSkinDrop(drop: { skinId: string; skinName: string; unitId: string; unitName: string }, idx = 0): string {
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const skin = configData?.skins?.[drop.skinId];
    const spriteKey = skin?.spriteKey ?? '';
    const portraitSrc = spriteKey ? `/activity/sprites/processed/players/${spriteKey}_sm.webp${CB}` : '';

    return `
      <div class="cc-drop-row cc-skin-drop" style="--drop-i:${idx}; --rarity-color:#b088d0">
        ${portraitSrc ? `<img class="cc-drop-skin-portrait" src="${portraitSrc}" alt="${drop.skinName}">` : '<div class="cc-drop-icon">✨</div>'}
        <div class="cc-drop-body">
          <div class="cc-drop-name" style="color: #b088d0">Skin: ${drop.skinName}</div>
          <div class="cc-drop-sub">Applied to ${drop.unitName}</div>
        </div>
      </div>
    `;
  }

  /** Parse explicit affixes into a compact inline summary */
  private parseAffixSummary(explicitAffixesJson: string | null | undefined, configData: ConfigData | undefined): string {
    if (!explicitAffixesJson) return '';
    try {
      const affixes: AffixInstance[] = JSON.parse(explicitAffixesJson);
      return affixes.map(inst => {
        const def: AffixDef | undefined = configData?.affixes?.[inst.affixId];
        if (!def) return '';
        if (def.category === 'flat_damage') {
          const el = formatElement(def.element!);
          return `<span class="cc-affix">+${inst.roll} ${el} Dmg</span>`;
        }
        return `<span class="cc-affix">${formatAffixValue(inst, def, configData)}</span>`;
      }).filter(Boolean).join('');
    } catch { return ''; }
  }

  /** Parse implicit + explicit affixes for items */
  private parseItemAffixSummary(item: ItemData, configData: ConfigData | undefined): string {
    return this.parseItemAffixSummaryFromRaw(item.implicitAffix, item.explicitAffixes, configData);
  }

  private parseItemAffixSummaryFromRaw(implicitJson: string | null | undefined, explicitsJson: string | null | undefined, configData: ConfigData | undefined): string {
    const parts: string[] = [];
    if (implicitJson) {
      try {
        const implicit: AffixInstance = JSON.parse(implicitJson);
        const def: AffixDef | undefined = configData?.affixes?.[implicit.affixId];
        if (def) {
          parts.push(`<span class="cc-affix cc-affix--implicit">${formatAffixValue(implicit, def, configData)}</span>`);
        }
      } catch { /* */ }
    }
    if (explicitsJson) {
      try {
        const affixes: AffixInstance[] = JSON.parse(explicitsJson);
        for (const inst of affixes) {
          const def: AffixDef | undefined = configData?.affixes?.[inst.affixId];
          if (!def) continue;
          if (def.category === 'flat_damage') {
            const el = formatElement(def.element!);
            parts.push(`<span class="cc-affix">+${inst.roll} ${el} Dmg</span>`);
          } else {
            parts.push(`<span class="cc-affix">${formatAffixValue(inst, def, configData)}</span>`);
          }
        }
      } catch { /* */ }
    }
    return parts.join('');
  }

  private async onCrawlDone(): Promise<void> {
    // Refresh state so main menu shows updated gold/xp
    const result = await APIManager.fetchUnifiedState();
    if (result.success && result.data) {
      StateManager.batchUpdate({
        playerData: result.data.playerData,
        combatData: result.data.combatData,
        crawlData: result.data.crawlData,
        eventCrawlData: result.data.eventCrawlData,
        campaignData: result.data.campaignData,
        configData: result.data.configData,
        unitData: result.data.unitData,
        itemData: result.data.itemData,
      });
    }
    ScreenRouter.replace('main-menu');
  }

  // ---------------------------------------------------------------------------
  // Timeline preview
  // ---------------------------------------------------------------------------

  private showTimelinePreview(ability: AbilityDef, targetId?: string): void {
    if (!this.speedTimeline) return;

    const combatData = StateManager.get('combatData');
    if (!combatData) return;

    const configData = StateManager.get('configData') as ConfigData | undefined;
    if (!configData) return;

    // Get momentum cost — check skill data on the active combatant first
    const active = combatData.combatants.find(c => c.id === combatData.activeCombatantId);
    const skill = active?.skills?.find(s => s.abilityId === ability.id);
    const momentumCost = skill?.momentumCost
      ?? configData.abilities[ability.id]?.momentumCost
      ?? 10000;

    // Check if ability applies a momentum penalty to the target (e.g. stagger)
    let targetMomentumPenalty = 0;
    const abilityConfig = configData.abilities[ability.id];
    if (targetId && abilityConfig?.applyTargetEffect) {
      const effect = configData.statusEffects?.[abilityConfig.applyTargetEffect];
      if (effect?.momentumPenalty) {
        targetMomentumPenalty = effect.momentumPenalty;
      }
    }

    const previewEntries = this.computePreviewEntries(combatData, momentumCost, targetId, targetMomentumPenalty);
    if (previewEntries) {
      this.speedTimeline.showPreview(previewEntries, combatData.activeCombatantId);
    }
  }

  private clearTimelinePreview(): void {
    if (this.pendingAbility) return; // Keep preview while targeting
    this.speedTimeline?.clearPreview();
  }

  private computePreviewEntries(
    combatData: ServerCombatState,
    momentumCost: number,
    targetId?: string,
    targetMomentumPenalty?: number,
  ): TimelineEntry[] | null {
    const THRESHOLD = 10000;
    const active = combatData.combatants.find(c => c.id === combatData.activeCombatantId);
    if (!active || !active.isAlive) return null;

    // Deep clone alive combatants for simulation
    const sim = combatData.combatants
      .filter(c => c.isAlive && c.spd > 0)
      .map(c => {
        let momentum = c.momentum;
        if (c.id === active.id) momentum -= momentumCost;
        if (c.id === targetId && targetMomentumPenalty) momentum -= targetMomentumPenalty;
        return {
          id: c.id,
          momentum,
          spd: c.spd,
          side: c.side,
          slotIndex: c.slotIndex,
          name: c.name,
          spriteKey: c.spriteKey,
        };
      });

    // Simulate forward, deduplicating so each combatant appears once
    const timeline: TimelineEntry[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < 20 && seen.size < sim.length; i++) {
      let minTicks = Infinity;
      for (const u of sim) {
        const needed = u.momentum >= THRESHOLD ? 0 : Math.ceil((THRESHOLD - u.momentum) / u.spd);
        if (needed < minTicks) minTicks = needed;
      }

      if (minTicks > 0) {
        for (const u of sim) {
          u.momentum += minTicks * u.spd;
        }
      }

      const ready = sim.filter(u => u.momentum >= THRESHOLD);
      ready.sort((a, b) => {
        if (a.momentum !== b.momentum) return b.momentum - a.momentum;
        if (a.side !== b.side) return a.side === 'player' ? -1 : 1;
        return a.slotIndex - b.slotIndex;
      });

      const winner = ready[0];
      if (!seen.has(winner.id)) {
        timeline.push({
          id: winner.id,
          label: winner.name[0],
          isEnemy: winner.side === 'enemy',
          isActive: winner.id === combatData.activeCombatantId,
          spriteKey: winner.spriteKey,
          momentum: winner.momentum,
          spd: winner.spd,
          side: winner.side,
          slotIndex: winner.slotIndex,
        });
        seen.add(winner.id);
      }

      // Always subtract momentum to continue simulation
      winner.momentum -= THRESHOLD;
    }

    return timeline;
  }

  // ---------------------------------------------------------------------------
  // Utility
  // ---------------------------------------------------------------------------

  private setButtonsEnabled(enabled: boolean): void {
    const buttons = this.elements.abilityButtons?.querySelectorAll('button') ?? [];
    if (!enabled) {
      buttons.forEach((btn) => {
        (btn as HTMLButtonElement).disabled = true;
      });
      return;
    }

    // When re-enabling, re-check focus affordability per ability
    const combatData = StateManager.get('combatData');
    const active = combatData?.combatants.find(c => c.id === combatData.activeCombatantId);

    buttons.forEach((btn) => {
      const htmlBtn = btn as HTMLButtonElement;
      const abilityId = htmlBtn.dataset.abilityId;
      if (active && abilityId) {
        const skill = active.skills?.find(g => g.abilityId === abilityId);
        const focusCost = skill?.focusCost ?? 0;
        const canAfford = active.focus >= focusCost;
        htmlBtn.disabled = !canAfford;
        htmlBtn.classList.toggle('insufficient-focus', !canAfford);
      } else {
        htmlBtn.disabled = false;
        htmlBtn.classList.remove('insufficient-focus');
      }
    });
  }

  private updateActionLog(payload: string | { message: string; type?: string; isCrit?: boolean; sourceSide?: 'player' | 'enemy' }): void {
    if (!this.elements.actionLog) return;
    const { message, type, isCrit, sourceSide } = typeof payload === 'string'
      ? { message: payload, type: undefined, isCrit: false, sourceSide: undefined }
      : { message: payload.message, type: payload.type, isCrit: !!payload.isCrit, sourceSide: payload.sourceSide };

    const category = this.classifyLogEvent(type, message, sourceSide);
    const div = document.createElement('div');
    div.className = `log-msg log-${category}`;
    if (isCrit) div.classList.add('log-crit');
    div.textContent = message;
    this.elements.actionLog.appendChild(div);
    // Trim to last 5 messages
    while (this.elements.actionLog.children.length > 5) {
      this.elements.actionLog.removeChild(this.elements.actionLog.children[0]);
    }
    // Auto-scroll to latest
    this.elements.actionLog.scrollTop = this.elements.actionLog.scrollHeight;
  }

  /** Map a CombatEventType (and attacker side) to a CSS category class so the
   *  log can color-code at a glance. Damage and DoT rows split by side — your
   *  hits read gold, incoming hits read red — otherwise damage events (~70% of
   *  any fight) collapse into a wall of identical red lines. */
  private classifyLogEvent(type: string | undefined, message: string, sourceSide?: 'player' | 'enemy'): string {
    switch (type) {
      case 'damage': return sourceSide === 'player' ? 'damage-out' : 'damage-in';
      case 'dot_tick': return sourceSide === 'player' ? 'dot-out' : 'dot-in';
      case 'heal': return 'heal';
      case 'buff':
      case 'cleanse': return 'buff';
      case 'debuff':
      case 'effect_expire': return 'debuff';
      case 'defeated': return sourceSide === 'enemy' ? 'death-ally' : 'death-foe';
      case 'summon': return 'summon';
      case 'flee_success':
      case 'flee_fail':
      case 'victory':
      case 'defeat': return 'system';
      default:
        if (/^Error:/i.test(message)) return 'error';
        if (/escaped|victory|defeated\b|wins|falls/i.test(message)) return 'system';
        return 'meta';
    }
  }

  private clearActionLog(): void {
    if (!this.elements.actionLog) {
      this.elements.actionLog = document.getElementById('action-log');
    }
    if (this.elements.actionLog) {
      this.elements.actionLog.innerHTML = '';
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (!this.localState) return;
    if (this.localState.isPlayingEvents) return;
    if (this.localState.phase !== 'combat') return;

    // Don't intercept if user is typing in an input
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

    const combatData = StateManager.get('combatData');
    if (!combatData) return;

    const active = combatData.combatants.find(c => c.id === combatData.activeCombatantId);
    if (!active || active.side !== 'player') return;

    if (e.key === 'Escape') {
      if (this.pendingAbility) {
        this.clearTargetingMode();
        e.preventDefault();
      }
      return;
    }

    const keyIndex = parseInt(e.key, 10) - 1;
    if (isNaN(keyIndex) || keyIndex < 0) return;

    if (this.pendingAbility) {
      // In targeting mode — keys select targets
      const isAllyTarget = this.pendingAbility.targeting === 'ally_single' || this.pendingAbility.targeting === 'ally_all';
      const side = isAllyTarget ? 'player' : 'enemy';
      const targets = combatData.combatants
        .filter(c => c.side === side && c.isAlive)
        .sort((a, b) => a.slotIndex - b.slotIndex);

      if (keyIndex < targets.length) {
        e.preventDefault();
        this.onTargetSelected(targets[keyIndex].id);
      }
    } else {
      // Not in targeting mode — keys select abilities
      if (keyIndex < this.currentAbilities.length) {
        e.preventDefault();
        this.onAbility(this.currentAbilities[keyIndex]);
      }
    }
  }

}
